export interface CoastFireInputs {
  currentAge: number
  retireAge: number
  currentSavings: number
  monthlyContrib: number
  annualExpenses: number
  returnRate: number // as decimal, e.g. 0.07
}

export interface ChartPoint {
  age: number
  portfolio: number
  threshold: number
}

export interface CoastFireResults {
  coastFireNumber: number
  coastAge: number | null // currentAge if already coasted; null if never reaches threshold
  yearsToCoast: number | null // null when already coasted or never reaches threshold
  alreadyCoasted: boolean
  projectedBalance: number
  monthlySurplus: number
  chartData: ChartPoint[]
}

export function calculateCoastFire(inputs: CoastFireInputs): CoastFireResults {
  const { currentAge, retireAge, currentSavings, monthlyContrib, annualExpenses, returnRate } = inputs
  const target = annualExpenses * 25
  const yearsToRetire = retireAge - currentAge

  if (yearsToRetire <= 0) {
    const isCoasted = currentSavings >= target
    return {
      coastFireNumber: target,
      coastAge: isCoasted ? currentAge : null,
      yearsToCoast: null,
      alreadyCoasted: isCoasted,
      projectedBalance: currentSavings,
      monthlySurplus: monthlyContrib,
      chartData: [{ age: currentAge, portfolio: Math.round(currentSavings), threshold: Math.round(target) }],
    }
  }

  const coastFireNumber = Math.round(target / Math.pow(1 + returnRate, yearsToRetire))
  const alreadyCoasted = currentSavings >= coastFireNumber
  const monthlyRate = returnRate / 12

  const chartData: ChartPoint[] = []
  let coastAge: number | null = null
  let projectedBalance = 0

  for (let y = 0; y <= yearsToRetire; y++) {
    const age = currentAge + y
    const yearsLeft = retireAge - age
    const threshold = Math.round(yearsLeft > 0 ? target / Math.pow(1 + returnRate, yearsLeft) : target)

    let portfolio = currentSavings
    const months = y * 12
    for (let m = 0; m < months; m++) {
      portfolio = portfolio * (1 + monthlyRate) + monthlyContrib
    }
    const portfolioRounded = Math.round(portfolio)

    chartData.push({ age, portfolio: portfolioRounded, threshold })

    if (coastAge === null && portfolioRounded >= threshold) {
      coastAge = age
    }

    if (y === yearsToRetire) {
      projectedBalance = portfolioRounded
    }
  }

  return {
    coastFireNumber,
    coastAge,
    yearsToCoast: alreadyCoasted || coastAge === null ? null : coastAge - currentAge,
    alreadyCoasted,
    projectedBalance,
    monthlySurplus: monthlyContrib,
    chartData,
  }
}
